import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, Check, X } from "lucide-react";
import { isNativeApp } from "@/lib/platform";

/**
 * Cookie / analytics consent banner.
 *
 * Under Dutch cookie law (art. 11.7a Telecommunicatiewet) and the EU
 * ePrivacy Directive, analytics that use tracking cookies require prior
 * consent. This component:
 *
 *  1. Shows a banner on first visit when no choice has been made yet.
 *  2. Only injects Google Analytics after "Accept" is clicked.
 *  3. Persists the choice in localStorage so it isn't asked again.
 *  4. Skips itself entirely inside the Capacitor app — the native app doesn't
 *     use HTTP cookies, and the app store guidelines already cover it.
 *
 * The user can revoke or update their choice later via the Privacy Policy
 * page (calls `resetCookieConsent()` exported below).
 */
const STORAGE_KEY = "cookie-consent:v1";       // "accepted" | "rejected"
const GA_MEASUREMENT_ID = "G-PREDQBSE7K";

function readConsent() {
    try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
}
function writeConsent(v) {
    try { localStorage.setItem(STORAGE_KEY, v); } catch { /* ignore */ }
}

/** Called from the Privacy Policy "manage cookies" link. */
export function resetCookieConsent() {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    // Reload so the banner reappears and any previously-injected GA is gone.
    window.location.reload();
}

/**
 * Injects gtag.js exactly once. Called only after the user accepts.
 */
function loadGoogleAnalytics() {
    if (window.__gaLoaded) return;
    window.__gaLoaded = true;
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    // eslint-disable-next-line no-inner-declarations
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = gtag;
    gtag("js", new Date());
    gtag("config", GA_MEASUREMENT_ID, { anonymize_ip: true });
}

export default function CookieConsent() {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (isNativeApp()) return;                       // no cookies in the app
        const current = readConsent();
        if (current === "accepted") {
            loadGoogleAnalytics();
            return;
        }
        if (current === "rejected") return;              // stay silent, no GA
        // No choice yet — show the banner after a short delay so it doesn't
        // fight the initial page paint.
        const t = setTimeout(() => setVisible(true), 500);
        return () => clearTimeout(t);
    }, []);

    if (isNativeApp()) return null;

    const accept = () => {
        writeConsent("accepted");
        loadGoogleAnalytics();
        setVisible(false);
    };
    const reject = () => {
        writeConsent("rejected");
        setVisible(false);
    };

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    key="banner"
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 40 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    role="dialog"
                    aria-label="Cookie consent"
                    data-testid="cookie-consent-banner"
                    className="fixed inset-x-0 bottom-0 z-[65] p-4 sm:p-6 pointer-events-none"
                    style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
                >
                    <div className="max-w-3xl mx-auto pointer-events-auto bg-white border border-border/60 rounded-3xl shadow-2xl overflow-hidden">
                        <div className="p-5 sm:p-6 flex flex-col sm:flex-row items-start gap-4">
                            <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                <Cookie className="w-5 h-5" strokeWidth={2.2} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-display text-lg font-semibold">We use analytics cookies</div>
                                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                                    We'd like to use Google Analytics with anonymised IPs to understand how the site is used and
                                    make it better. It's completely optional — the site works perfectly either way. See our{" "}
                                    <Link to="/privacy" className="text-primary hover:underline" data-testid="cookie-privacy-link">
                                        Privacy Policy
                                    </Link>{" "}
                                    for the details.
                                </p>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto shrink-0">
                                <button
                                    type="button"
                                    data-testid="cookie-reject-btn"
                                    onClick={reject}
                                    className="order-2 sm:order-1 h-10 px-5 rounded-full text-sm font-semibold border border-border bg-white text-foreground hover:bg-muted transition-colors inline-flex items-center justify-center gap-1.5"
                                >
                                    <X className="w-4 h-4" /> Reject
                                </button>
                                <button
                                    type="button"
                                    data-testid="cookie-accept-btn"
                                    onClick={accept}
                                    className="order-1 sm:order-2 h-10 px-5 rounded-full text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors inline-flex items-center justify-center gap-1.5"
                                >
                                    <Check className="w-4 h-4" /> Accept
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
